import startOfDay from 'date-fns/startOfDay';
import max from 'date-fns/max';
import sortBy from 'lodash/sortBy';

import { PATIENT_STATUS, REASON_FOR_VISIT } from '../constants';

import toDate from './toDate';
import toISODateString from './toISODateString';
import { updateParams, getQueryFromLocation } from './urls';

function toId(obj) {
  if (obj == null) {
    return '';
  }
  if (typeof obj === 'string') {
    return obj;
  }
  if (obj.id != null) {
    return obj.id;
  }
  return String(obj);
}

function toLegacyPatientStatusId(patientStatus) {
  const patientStatusId = toId(patientStatus).toLowerCase();
  if (patientStatusId === 'new' || patientStatusId === 'returning') return patientStatusId;
  return '';
}

function toPatientStatus(value) {
  return value && value.toUpperCase() === PATIENT_STATUS.RETURNING
    ? PATIENT_STATUS.RETURNING
    : PATIENT_STATUS.NEW;
}

function isForceSelect(reasonForVisitId) {
  return reasonForVisitId === REASON_FOR_VISIT.SELECT;
}

export default function getSchedulingParams({
  history,
  reasonsForVisit = [],
  defaultReasonForVisit,
  autoSelect = false,
}) {
  const parsedQuery = getQueryFromLocation(history.location);
  const today = startOfDay(new Date());
  const selectedReasonForVisitId =
    parsedQuery.reasonForVisitId || parsedQuery.reason_for_visit_id || '';
  let reasonForVisit = reasonsForVisit.find(reason => reason.id === selectedReasonForVisitId);
  const patientStatus = toPatientStatus(parsedQuery.patientStatusId);

  if (autoSelect && !reasonForVisit && !isForceSelect(selectedReasonForVisitId)) {
    if (patientStatus === PATIENT_STATUS.NEW && defaultReasonForVisit) {
      reasonForVisit = defaultReasonForVisit;
    } else {
      reasonForVisit = sortBy(reasonsForVisit, reason => reason.name).find(
        reason => reason.patientStatus === patientStatus
      );
    }
  }

  const minDate = max([startOfDay(toDate(parsedQuery.minDate) || today), today]);
  const selectedDate = max([startOfDay(toDate(parsedQuery.after) || today), minDate]);
  const values = {
    reasonsForVisit,
    // Patient status enum, i.e. "NEW"
    patientStatus,
    reasonForVisit,
    minDate,
    selectedDate,
  };
  if (values.reasonForVisit) {
    values.patientStatus = values.reasonForVisit.patientStatus;
  }
  const query = {
    // Legacy lowercased patient status that matches the URL, i.e. "new"
    // TODO: Update all URLs to follow new standard, i.e. `?patientStatus=RETURNING`
    patientStatusId: toLegacyPatientStatusId(values.patientStatus),
    reasonForVisitId: isForceSelect(selectedReasonForVisitId)
      ? selectedReasonForVisitId
      : toId(values.reasonForVisit),
    after: toISODateString(values.selectedDate),
    minDate: toISODateString(values.minDate),
  };

  if (parsedQuery.reason_for_visit_id) query.reason_for_visit_id = null;
  if (
    parsedQuery.patientStatusId !== query.patientStatusId ||
    selectedReasonForVisitId !== query.reasonForVisitId ||
    parsedQuery.after !== query.after ||
    parsedQuery.minDate !== query.minDate
  ) {
    updateParams(history, query, { replace: true });
  }

  return {
    values,
    query,
    updatePatientStatus: value =>
      updateParams(
        history,
        {
          patientStatusId: toLegacyPatientStatusId(value),
          reasonForVisitId: REASON_FOR_VISIT.SELECT,
        },
        { replace: true }
      ),
    updateReasonForVisit: value =>
      updateParams(history, { reasonForVisitId: toId(value) }, { replace: true }),
    updateSelectedDate: value =>
      updateParams(history, { after: toISODateString(value) }, { replace: true }),
  };
}
